/**
 * Merchant Pro V1 PR-4a — canonical chair-grid table card.
 *
 * Operator-locked layout (2026-06-18 PR-4a auth §3):
 *
 *   [Chair]    [Chair]      ← top row chairs
 *      VIP A1
 *       2 / 4
 *   [Chair]    [Chair]      ← bottom row chairs
 *
 * Chairs BRACKET the label vertically. Active chairs use the state hex
 * color (§8.2 of the Review Package). Inactive chairs use #374151 at
 * 60 % opacity. Fill order is row-major top-left → top-right →
 * bottom-left → bottom-right.
 *
 * Capacity rules (Phase E3 PR4d-6 — multi-row extension):
 *   • capacity ≤ 4  → unchanged baseline (2-up / 2×2). No regression.
 *   • capacity = 5  → 3 + 2 (top-heavy reads naturally).
 *   • capacity = 6  → 3 + 3 (balanced two-row).
 *   • capacity = 7  → 4 + 3 (top-heavy).
 *   • capacity = 8  → 4 + 4 (balanced two-row).
 *   • capacity = 9  → 3 + 3 + 3 (three balanced rows).
 *   • capacity = 10 → 3 + 4 + 3 (operator preference per Phase E3 §scope).
 *   • capacity = 11 → 4 + 4 + 3.
 *   • capacity = 12 → 4 + 4 + 4 (three full rows).
 *   • capacity > 12 → 4 + 4 + 4 + `+N` overflow badge for the remainder
 *                     (visible-chair ceiling = 12).
 *
 * Shared component — consumed by LiveFloorPlanPage AND ManualBookingPage S3.
 * Single visual language. No second floor-plan engine.
 */
import { Armchair, Lock, BrushCleaning, Gamepad2, Clock, UserCheck, Check } from 'lucide-react';

/* Operator-locked palette (§8.2 + PR-4a auth §1). */
export const STATE_PALETTE = {
  available:  '#22C55E',
  reserved:   '#FBBF24',
  checked_in: '#3B82F6',
  playing:    '#8B5CF6',
  cleaning:   '#F97316',
  blocked:    '#EF4444',
};

const INACTIVE_CHAIR = '#374151';   // gray-700
const INACTIVE_OPACITY = 0.6;

const STATE_ICON = {
  available:  null,
  reserved:   Clock,
  checked_in: UserCheck,
  playing:    Gamepad2,
  cleaning:   BrushCleaning,
  blocked:    Lock,
};

const STATE_LABEL = {
  available:  'Available',
  reserved:   'Reserved',
  checked_in: 'Checked-In',
  playing:    'Playing',
  cleaning:   'Cleaning',
  blocked:    'Blocked',
};

/* PR4d-6 — Visible-chair ceiling. Capacity > 12 collapses to 12 visible
 * chairs + an overflow `+N` badge so the card height stays bounded. */
const VISIBLE_CHAIR_CEILING = 12;

/**
 * Phase E3 PR4d-6 — Compute the chair-row layout for a given capacity.
 *
 * Returns an array of row sizes (e.g. `[3, 4, 3]` for capacity 10) with
 * row-major fill order. Capacity > 12 caps at the ceiling; the caller is
 * responsible for the overflow badge.
 *
 * The matrix is operator-locked. Do not "improve" rows without a brief.
 */
export function buildChairLayout(capacity) {
  const cap = Math.max(0, Number(capacity) || 0);
  const visible = Math.min(cap, VISIBLE_CHAIR_CEILING);
  switch (visible) {
    case 0:  return [];
    case 1:  return [1];
    case 2:  return [2];
    case 3:  return [2, 1];
    case 4:  return [2, 2];
    case 5:  return [3, 2];
    case 6:  return [3, 3];
    case 7:  return [4, 3];
    case 8:  return [4, 4];
    case 9:  return [3, 3, 3];
    case 10: return [3, 4, 3];
    case 11: return [4, 4, 3];
    case 12: return [4, 4, 4];
    default: return [4, 4, 4]; // safety fallback (unreachable — visible ≤ 12)
  }
}

/**
 * Compute which chair slots are active (state-colored) given occupancy.
 *
 * Row-major fill order matches `buildChairLayout`: row 0 fills first
 * (left → right), then row 1, etc. Returns an array of booleans of
 * length `totalChairs`.
 */
export function computeChairFill(occupancy, capacity) {
  const layout = buildChairLayout(capacity);
  const total = layout.reduce((acc, n) => acc + n, 0);
  const filled = Math.min(Math.max(0, occupancy ?? 0), total);
  return Array.from({ length: total }, (_, i) => i < filled);
}

/**
 * Render a single chair icon.
 *
 * Active chairs use the state hex color (100 % opacity). Inactive chairs
 * use the gray-700 at 60 % opacity. Size is variant-driven (48 px chairs
 * for the canonical 'md' card, 32 px chairs for the 'sm' thumbnail).
 */
function ChairIcon({ active, stateColor, testId, chairSize = 22 }) {
  const color = active ? stateColor : INACTIVE_CHAIR;
  const opacity = active ? 1 : INACTIVE_OPACITY;
  return (
    <Armchair
      size={chairSize}
      color={color}
      strokeWidth={2}
      style={{ opacity }}
      data-testid={testId}
      data-active={active ? 'true' : 'false'}
    />
  );
}

/* PR-4c size variants — operator-locked per OQ-PR4c-2 (chair: 22 / 14). */
const SIZE_PRESETS = {
  md: {
    chair: 22,
    label: 'text-base',
    occupancy: 'text-xs',
    pill: 'text-[10px]',
    pad: 'p-4 pt-5 pb-5',
    gap: 'gap-3',
    rowGap: 'gap-2',
    minH: undefined,
  },
  sm: {
    chair: 14,
    label: 'text-xs',
    occupancy: 'text-[10px]',
    pill: 'text-[9px]',
    pad: 'p-2 pt-2.5 pb-2.5',
    gap: 'gap-1.5',
    rowGap: 'gap-1',
    minH: 100,
  },
  /* E2 PR4d-3 / PR4d-4 — inline-row thumbnail variant (32-px footprint).
   * Renders ONLY the chairs in their multi-row layout; label, occupancy
   * counter, state pill, state icon are all suppressed. State coloring
   * on chairs is preserved so operators can glance-read the bound
   * table's state from a list row. Used by ReservationsListPage +
   * WalkInWaitlistPage. PR4d-6 — chair size shrinks proportionally;
   * row count matches the full-size variant. */
  xs: {
    chair: 8,
    label: 'hidden',
    occupancy: 'hidden',
    pill: 'hidden',
    pad: 'p-0',
    gap: 'gap-0',
    rowGap: 'gap-0.5',
    minH: undefined,
  },
};

export default function ChairGridTableCard({
  table,
  selected = false,
  onClick = null,
  showStatePill = true,
  showLegendIcon = true,
  testIdPrefix = 'floor-plan-table',
  size = 'md',
}) {
  if (!table) return null;

  const preset = SIZE_PRESETS[size] || SIZE_PRESETS.md;
  const state = table.state || 'available';
  // Phase V1.1-WP5 (OQ-V1.1-5 LOCKED) — when the bound reservation's
  // session is paused, the card paints with the `reserved` (amber)
  // palette so operators can distinguish active-playing (purple) from
  // paused (amber) at a glance. Static — no animation. The underlying
  // table.state stays `playing` (no pause-cascade to table.state); this
  // is a presentational override only.
  const sessionState = table.current_session_state || null;
  const effectiveState = sessionState === 'paused' ? 'reserved' : state;
  const stateColor = STATE_PALETTE[effectiveState] ?? STATE_PALETTE.available;
  const capacity = Number(table.capacity || 0);
  const occupancy = Number(
    table.current_party_size != null
      ? table.current_party_size
      : (state === 'reserved' || state === 'checked_in' || state === 'playing' ? capacity : 0),
  );

  // PR4d-6 — multi-row layout. `layout` is e.g. [3, 4, 3] for capacity 10.
  // `overflow` only triggers when capacity exceeds the visible ceiling
  // (12 chairs); legacy `capacity > 4 → +N` behaviour is retired in
  // favour of "render as many rows as needed up to the ceiling".
  const layout = buildChairLayout(capacity);
  const totalVisible = layout.reduce((acc, n) => acc + n, 0);
  const overflow = capacity > VISIBLE_CHAIR_CEILING
    ? capacity - VISIBLE_CHAIR_CEILING
    : 0;
  const fill = computeChairFill(occupancy, capacity);
  const StateIcon = STATE_ICON[effectiveState];
  const label = table.name || table.label || table.id || 'Table';

  // ----- Row splitter -----
  // Split the flat row-major fill[] array into per-row slices so each
  // row renders with its own justify-between spacing.
  const rowSlices = [];
  let cursor = 0;
  for (const rowSize of layout) {
    rowSlices.push(fill.slice(cursor, cursor + rowSize));
    cursor += rowSize;
  }

  // Center-row index: the label + occupancy block sits at the midpoint
  // of the rows so the chairs visually bracket the label (preserving
  // the PR-4a "chairs bracket the label" rule). For odd-row layouts
  // we insert AFTER floor(rows/2), giving balanced bracketing. For
  // even-row layouts we insert at the midpoint (rows/2).
  const labelInsertAfter = Math.floor(layout.length / 2);

  // Card visuals — dark background, colored border 60 % alpha, soft shadow.
  const cardStyle = {
    background: '#15151B',
    border: `1.5px solid ${stateColor}99`, // 60 % alpha
    boxShadow: selected
      ? `0 0 0 2px ${stateColor}, 0 8px 32px ${stateColor}33`
      : '0 4px 12px rgba(0,0,0,0.35)',
    ...(preset.minH ? { minHeight: preset.minH } : {}),
  };

  // ----- Render a single chair row -----
  const renderChairRow = (chairs, rowIdx) => {
    if (chairs.length === 0) return null;
    // Single-chair row renders one centered chair (no justify-between).
    const justifyClass = chairs.length === 1 ? 'justify-center' : 'justify-between';
    return (
      <div
        key={`row-${rowIdx}`}
        data-testid={`${testIdPrefix}-card-${table.id}-chair-row-${rowIdx}`}
        className={`flex items-center ${justifyClass} w-full px-2`}
      >
        {chairs.map((active, colIdx) => (
          <ChairIcon
            key={`r${rowIdx}c${colIdx}`}
            active={active}
            stateColor={stateColor}
            testId={`${testIdPrefix}-card-${table.id}-chair-r${rowIdx}-c${colIdx}`}
            chairSize={preset.chair}
          />
        ))}
      </div>
    );
  };

  return (
    <button
      type="button"
      onClick={onClick}
      disabled={!onClick}
      data-testid={`${testIdPrefix}-card-${table.id}`}
      data-state={state}
      data-size={size}
      data-occupancy={`${occupancy}/${capacity}`}
      data-chair-rows={layout.length}
      data-chair-layout={layout.join('-')}
      className={`relative flex flex-col items-center justify-between ${preset.gap} rounded-2xl ${preset.pad} transition-transform duration-150 hover:scale-[1.02] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-offset-[#0A0A0A] focus:ring-gold/60 text-left w-full disabled:cursor-default`}
      style={cardStyle}
    >
      {/* Selected checkmark badge — top right */}
      {selected ? (
        <span
          data-testid={`${testIdPrefix}-card-${table.id}-selected-badge`}
          className="absolute top-2 right-2 w-6 h-6 rounded-full grid place-items-center"
          style={{ background: '#D4AF37' }}
        >
          <Check size={14} color="#0A0A0A" strokeWidth={3} />
        </span>
      ) : null}

      {/* State icon top-left */}
      {showLegendIcon && StateIcon ? (
        <span
          data-testid={`${testIdPrefix}-card-${table.id}-state-icon`}
          className="absolute top-2 left-2"
          style={{ color: stateColor, opacity: 0.85 }}
        >
          <StateIcon size={size === 'sm' ? 11 : 14} />
        </span>
      ) : null}

      {/* Overflow badge for capacity > 12 (visible-chair ceiling) */}
      {overflow > 0 ? (
        <span
          data-testid={`${testIdPrefix}-card-${table.id}-overflow-badge`}
          className="absolute top-2 right-8 text-[10px] font-bold px-1.5 py-0.5 rounded-md uppercase tracking-wider"
          style={{ background: stateColor + '33', color: stateColor, border: `1px solid ${stateColor}66` }}
        >
          +{overflow}
        </span>
      ) : null}

      {/* Multi-row chair grid — rows render top→bottom with the label
          block injected at the layout midpoint so chairs bracket it. */}
      <div className={`flex flex-col items-center w-full ${preset.rowGap}`}>
        {rowSlices.flatMap((chairs, rowIdx) => {
          const out = [renderChairRow(chairs, rowIdx)];
          // Inject the label/occupancy block right after the
          // midpoint row so chairs visually bracket the label.
          // Suppressed entirely in the xs thumbnail variant.
          if (size !== 'xs' && layout.length > 1 && rowIdx === labelInsertAfter - 1) {
            out.push(
              <div
                key={`label-${rowIdx}`}
                className="flex flex-col items-center gap-0.5 my-1"
              >
                <div
                  data-testid={`${testIdPrefix}-card-${table.id}-label`}
                  className={`${preset.label} font-semibold text-center leading-tight`}
                  style={{ color: stateColor, opacity: 0.95 }}
                >
                  {label}
                </div>
                <div
                  data-testid={`${testIdPrefix}-card-${table.id}-occupancy`}
                  className={`${preset.occupancy} font-mono tabular-nums`}
                  style={{ color: stateColor, opacity: 0.75 }}
                >
                  {occupancy} / {capacity}
                </div>
              </div>,
            );
          }
          return out;
        })}
        {/* Edge case: capacity ≤ 2 — only one chair row, label renders
            after all chairs. */}
        {size !== 'xs' && layout.length <= 1 ? (
          <div className="flex flex-col items-center gap-0.5 my-1">
            <div
              data-testid={`${testIdPrefix}-card-${table.id}-label`}
              className={`${preset.label} font-semibold text-center leading-tight`}
              style={{ color: stateColor, opacity: 0.95 }}
            >
              {label}
            </div>
            <div
              data-testid={`${testIdPrefix}-card-${table.id}-occupancy`}
              className={`${preset.occupancy} font-mono tabular-nums`}
              style={{ color: stateColor, opacity: 0.75 }}
            >
              {occupancy} / {capacity}
            </div>
          </div>
        ) : null}
      </div>

      {/* State pill at the very bottom — optional */}
      {showStatePill ? (
        <span
          data-testid={`${testIdPrefix}-card-${table.id}-state-pill`}
          className={`${preset.pill} font-semibold uppercase tracking-wider px-2 py-0.5 rounded-full`}
          style={{
            background: stateColor + '22',
            color: stateColor,
            border: `1px solid ${stateColor}55`,
          }}
        >
          {STATE_LABEL[state] ?? state}
        </span>
      ) : null}
    </button>
  );
}

/**
 * Lightweight legend strip — renders the 6 state chips with palette
 * colors. Consumed by LiveFloorPlanPage.
 */
export function FloorPlanLegend({ testId = 'floor-plan-legend' }) {
  const entries = Object.entries(STATE_PALETTE);
  return (
    <div
      data-testid={testId}
      className="flex flex-wrap items-center gap-3 px-4 py-3 rounded-2xl bg-[#0E0E13] border border-white/5"
    >
      <span className="text-xs uppercase tracking-wider text-white/50">Legend</span>
      {entries.map(([state, color]) => {
        const Icon = STATE_ICON[state];
        return (
          <span
            key={state}
            data-testid={`${testId}-chip-${state}`}
            className="flex items-center gap-2 px-2.5 py-1 rounded-full"
            style={{ background: color + '1A', color, border: `1px solid ${color}55` }}
          >
            {Icon ? <Icon size={12} /> : <span style={{ width: 12, height: 12, borderRadius: '50%', background: color }} />}
            <span className="text-[11px] font-semibold uppercase tracking-wide">{STATE_LABEL[state]}</span>
          </span>
        );
      })}
    </div>
  );
}
